import React from 'react';
import { getYouTubeId } from './utils';

export const Chapter = ({ n, label, light }) => (
  <div className="flex items-center gap-3 font-wvc-body">
    {n && <span className={`font-wvc-display text-4xl md:text-5xl font-light leading-none ${light ? 'text-[#C6A87C]' : 'text-[#C6A87C]'}`}>{String(n).padStart(2, '0')}</span>}
    <span className={`text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold ${light ? 'text-[#FDFCF8]/60' : 'text-[#1A3626]/60'}`}>{label}</span>
  </div>
);

export const StoryHero = ({ hero, stats, name }) => {
  const videoId = getYouTubeId(hero?.video_url);
  return (
    <>
      <section className="relative h-[78vh] md:h-[86vh] flex items-end overflow-hidden bg-[#0A1710]" data-testid="story-hero">
        {videoId && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&rel=0&playsinline=1&modestbranding=1&iv_load_policy=3&disablekb=1&cc_load_policy=0&cc_lang_pref=en`}
              title={name}
              className="absolute top-1/2 left-1/2 border-0"
              style={{
                width: 'max(100%, 177.78vh)',
                height: 'max(100%, 56.25vw)',
                transform: 'translate(-50%, -50%) scale(1.35)'
              }}
              allow="autoplay; encrypted-media"
              tabIndex={-1}
              aria-hidden="true"
            />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1710]/85 via-[#0A1710]/20 to-[#0A1710]/40" />
        <div className="relative w-full font-wvc-body">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pb-12 md:pb-16">
            <div className="wvc-fade-up flex items-center gap-4">
              <span className="h-px w-10 bg-[#C6A87C]" />
              <p className="text-[#C6A87C] uppercase tracking-[0.35em] text-[10px] md:text-xs font-bold" data-testid="story-hero-name">{name}</p>
            </div>
            <h1 className="wvc-fade-up-delay-1 font-wvc-display text-5xl sm:text-6xl lg:text-7xl text-[#FDFCF8] font-light leading-[0.95] tracking-tighter mt-5 max-w-4xl" data-testid="story-hero-headline">
              {hero?.headline}
            </h1>
            {hero?.intro && <p className="wvc-fade-up-delay-2 text-[#FDFCF8]/70 mt-5 max-w-lg text-sm md:text-base font-light">{hero.intro}</p>}
          </div>
        </div>
      </section>

      {stats?.length > 0 && (
        <section className="bg-[#1A3626] border-t border-[#2A4D38] font-wvc-body" data-testid="story-stats">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-2 md:grid-cols-4">
            {stats.map((s, i) => (
              <div key={i} className={`py-8 md:py-12 px-5 border-[#2A4D38] ${i > 0 ? 'border-l' : ''} ${i >= 2 ? 'max-md:border-t max-md:[&:nth-child(3)]:border-l-0' : ''}`} data-testid={`story-stat-${i}`}>
                <div className="font-wvc-display text-4xl md:text-6xl font-light text-[#FDFCF8] tracking-tight tabular-nums">{s.value}</div>
                <div className="text-[9px] md:text-[10px] uppercase tracking-[0.25em] font-bold text-[#C6A87C] mt-3">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

export const TourVideo = ({ tour, subheadline }) => {
  const videoId = getYouTubeId(tour?.video_url);
  if (!videoId) return null;
  return (
    <section className="bg-[#FDFCF8] py-16 md:py-24 font-wvc-body" data-testid="story-tour">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-5">
          <Chapter n={1} label={tour.kicker || 'Step Inside'} />
          <h2 className="font-wvc-display text-4xl sm:text-5xl font-light text-[#0A1710] tracking-tight leading-[1.05] mt-6">{tour.title}</h2>
          {subheadline && <p className="text-[#1A3626]/70 mt-5 text-sm md:text-base font-light leading-relaxed max-w-md">{subheadline}</p>}
        </div>
        <div className="lg:col-span-7 aspect-video bg-[#F3EFEB] overflow-hidden border border-[#E5DCCF]">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?rel=0&cc_load_policy=0`}
            title={tour.title || 'Venue tour'}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};
